"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { BookOpen, LayoutDashboard, LogOut, Menu, ShieldCheck, UserRound, X } from "lucide-react";
import { useEffect, useState } from "react";
import { buttonVariants } from "@/components/ui/button";
import { clearAuth, getStoredUser } from "@/lib/auth";
import { cn } from "@/lib/utils";
import type { User } from "@/types";

const links = [
  { href: "/courses", label: "Khóa học" },
  { href: "/vocabulary", label: "Flashcard" },
  { href: "/blog", label: "Blog" }
];

export function SiteHeader() {
  const [user, setUser] = useState<User | null>(null);
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    setUser(getStoredUser());
  }, [pathname]);

  const logout = () => {
    clearAuth();
    setUser(null);
    router.push("/");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-ink/10 bg-porcelain/90 backdrop-blur-xl">
      <div className="container-page flex h-16 items-center justify-between">
        <Link href="/" className="flex items-center gap-2 font-black text-ink">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cinnabar-600 text-white">
            <BookOpen className="h-5 w-5" />
          </span>
          ZhongLearn
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={cn(
                "rounded-xl px-3 py-2 text-sm font-semibold text-muted hover:bg-white hover:text-ink",
                pathname === link.href && "bg-white text-cinnabar-700 shadow-line"
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          {user ? (
            <>
              <Link
                href={user.role === "ADMIN" ? "/admin" : "/dashboard"}
                className={buttonVariants("outline", "px-3")}
              >
                {user.role === "ADMIN" ? <ShieldCheck className="h-4 w-4" /> : <LayoutDashboard className="h-4 w-4" />}
                {user.role === "ADMIN" ? "Admin" : "Dashboard"}
              </Link>
              <button className={buttonVariants("ghost", "px-3")} onClick={logout} aria-label="Đăng xuất">
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link href="/login" className={buttonVariants("ghost")}>
                Đăng nhập
              </Link>
              <Link href="/register" className={buttonVariants("primary")}>
                Học thử
              </Link>
            </>
          )}
        </div>

        <button className="rounded-xl border border-ink/10 bg-white p-2 md:hidden" onClick={() => setOpen((value) => !value)}>
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open ? (
        <div className="border-t border-ink/10 bg-porcelain px-4 py-4 md:hidden">
          <div className="flex flex-col gap-2">
            {links.map((link) => (
              <Link key={link.href} href={link.href} className="rounded-xl bg-white px-3 py-3 text-sm font-semibold" onClick={() => setOpen(false)}>
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link href={user.role === "ADMIN" ? "/admin" : "/dashboard"} className="rounded-xl bg-white px-3 py-3 text-sm font-semibold">
                  {user.role === "ADMIN" ? "Admin" : "Dashboard"}
                </Link>
                <button className="rounded-xl bg-white px-3 py-3 text-left text-sm font-semibold" onClick={logout}>
                  Đăng xuất
                </button>
              </>
            ) : (
              <Link href="/login" className="rounded-xl bg-cinnabar-600 px-3 py-3 text-center text-sm font-semibold text-white">
                Đăng nhập
              </Link>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}
